import NiceModal from '@ebay/nice-modal-react'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import CloseIcon from '@mui/icons-material/Close'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import {
  Avatar,
  Box,
  Button,
  Chip,
  SortDirection,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { orange } from '@mui/material/colors'
import {
  CompanyUsersStatus,
  CompanyUserWithUserAndRole
} from '@shared/types/companies.types'
import { Permission } from '@shared/types/roles.types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import EmptyInvitations from '../../assets/images/EmptyInvitation.svg'
import ContainedAvatar from '../../components/ContainedAvatar'
import EmptySearchResults from '../../components/EmptySearchResults'
import SearchInput from '../../components/forms/SearchInput'
import TableLoader from '../../components/loaders/TableLoader'
import { RejectInvitationModal } from '../../components/modals/RejectInvitationModal'
import { useAuthStore } from '../../hooks/useAuthStore'
import {
  useCompanyUser,
  useCompanyUsers
} from '../../hooks/useQueries/useCompanies'
import { parseTimestamp } from '../../utils/date.utils'
import { getAvatarName } from '../../utils/user.utils'

type CompaniesReceivedInvitationsSortColumn =
  | 'name'
  | 'email'
  | 'role'
  | 'createdAt'
  | ''

const buttonStyles = {
  textTransform: 'none',
  fontSize: '.875rem',
  padding: '.375rem 1rem',
  minWidth: 'auto',
  borderRadius: '.25rem'
}

export default function CompaniesReceivedInvitations() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const companyId = params.companyId!
  const { data: members, isLoading: isLoadingMembers } = useCompanyUsers(
    companyId,
    {
      status: CompanyUsersStatus.PENDING_COMPANY
    }
  )
  const [searchText, setSearchText] = useState('')
  const [orderBy, setOrderBy] =
    useState<CompaniesReceivedInvitationsSortColumn>('')
  const [orderDirection, setOrderDirection] = useState<SortDirection>('asc')
  const [filteredMembers, setFilteredMembers] = useState<
    CompanyUserWithUserAndRole[]
  >([])
  const [lastAppliedFilters, setLastAppliedFilters] = useState({
    searchText: ''
  })
  const { userId } = useAuthStore()
  const { data: user, isLoading: isLoadingCompanyUser } = useCompanyUser(
    companyId,
    userId!
  )

  const { canManageMembers, canCreateMembers } = useMemo(() => {
    return {
      canManageMembers:
        user?.permissions?.includes(Permission.USERS_ACCEPT) ||
        user?.role?.fullAccess,
      canCreateMembers:
        user?.permissions?.includes(Permission.USERS_CREATE) ||
        user?.role?.fullAccess
    }
  }, [user])

  useEffect(() => {
    if (user && !canManageMembers) {
      navigate('/access-denied')
    }
  }, [canManageMembers, navigate, user])

  useEffect(() => {
    setFilteredMembers(members || [])
  }, [members])

  const handleSort = (column: CompaniesReceivedInvitationsSortColumn) => {
    let newOrderDirection: SortDirection = 'asc'

    if (orderBy === column) {
      newOrderDirection = orderDirection === 'asc' ? 'desc' : 'asc'
    }

    setOrderBy(column)
    setOrderDirection(newOrderDirection)

    const sorted = [...filteredMembers].sort((a, b) => {
      if (column === 'createdAt') {
        const getDateValue = (member: CompanyUserWithUserAndRole) => {
          return member.createdAt ? new Date(member.createdAt).getTime() : 0
        }
        const valA = getDateValue(a)
        const valB = getDateValue(b)

        return newOrderDirection === 'asc' ? valA - valB : valB - valA
      } else {
        const getStringValue = (member: CompanyUserWithUserAndRole) => {
          switch (column) {
            case 'name':
              return `${member.user.name} ${member.user.lastName}`.toLowerCase()
            case 'email':
              return member.user.email.toLowerCase()
            case 'role':
              return member.role.name.toLowerCase()
            default:
              return ''
          }
        }
        const valA = getStringValue(a)
        const valB = getStringValue(b)

        return newOrderDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
    })

    setFilteredMembers(sorted)
  }

  const handleFilter = (searchValue: string) => {
    const result =
      members?.filter(member => {
        const matchSearch = JSON.stringify(member)
          .toLowerCase()
          .includes(searchValue.toLowerCase())

        return matchSearch
      }) || []

    setFilteredMembers(result)
    setLastAppliedFilters({ searchText: searchValue })
  }

  const areFiltersEqual = (
    current: typeof lastAppliedFilters,
    last: typeof lastAppliedFilters
  ) => {
    return current.searchText === last.searchText
  }

  if (isLoadingCompanyUser || isLoadingMembers) {
    return <TableLoader totalColumns={5} totalRows={3} />
  }

  return (
    <>
      {members?.length ? (
        <TableContainer>
          <Stack direction='column' spacing='0.75rem'>
            <Stack
              direction='row'
              justifyContent='space-between'
              sx={{ paddingTop: '1.5rem', paddingBottom: '.75rem' }}
            >
              <Stack direction='row' spacing='1rem' alignItems='center'>
                <SearchInput
                  searchText={searchText}
                  setSearchText={setSearchText}
                  handleFilter={handleFilter}
                />

                <Button
                  variant='text'
                  color='primary'
                  onClick={() => handleFilter(searchText)}
                  disabled={
                    !searchText ||
                    areFiltersEqual({ searchText }, lastAppliedFilters)
                  }
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.search')}
                </Button>
                {searchText && (
                  <Button
                    startIcon={<CloseIcon />}
                    onClick={() => {
                      setSearchText('')
                      setFilteredMembers(members || [])
                      setLastAppliedFilters({
                        searchText: ''
                      })
                    }}
                    sx={{
                      textTransform: 'none',
                      color: 'secondary.main'
                    }}
                  >
                    {t('common.delete_filters')}
                  </Button>
                )}
              </Stack>
              {canCreateMembers && (
                <Button
                  variant='text'
                  color='primary'
                  startIcon={<PersonAddAltOutlinedIcon />}
                  component={Link}
                  to={`/companies/${companyId}/invitations/create`}
                  sx={{ textTransform: 'none', fontSize: '1rem' }}
                >
                  {t('members_view.invite_button')}
                </Button>
              )}
            </Stack>

            <Box sx={{ marginTop: '0.75rem' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sortDirection={
                        orderBy === 'name' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={
                          orderBy === 'name' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('name')}
                      >
                        {t('invitations_view.received.table_headers.user')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        orderBy === 'email' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={
                          orderBy === 'email' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('email')}
                      >
                        {t('invitations_view.received.table_headers.email')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      sortDirection={
                        orderBy === 'createdAt' ? orderDirection : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === 'createdAt'}
                        direction={
                          orderBy === 'createdAt' && orderDirection !== false
                            ? (orderDirection as 'asc' | 'desc')
                            : undefined
                        }
                        hideSortIcon={false}
                        onClick={() => handleSort('createdAt')}
                      >
                        {t(
                          'invitations_view.received.table_headers.created_at'
                        )}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      {t('invitations_view.received.table_headers.status')}
                    </TableCell>
                    <TableCell align='right'>
                      {t('invitations_view.received.table_headers.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMembers.length === 0 && searchText ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <EmptySearchResults />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMembers.map((member, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Stack
                            direction='row'
                            spacing='.5rem'
                            alignItems='center'
                          >
                            {member.user.avatarUri ? (
                              <ContainedAvatar
                                src={member.user.avatarUri}
                                alt={member.user.name}
                                sx={{ width: '2.5rem', height: '2.5rem' }}
                              />
                            ) : (
                              <Avatar
                                sx={{ width: '2.5rem', height: '2.5rem' }}
                              >
                                {getAvatarName(
                                  member.user.name || '',
                                  member.user.lastName || ''
                                )}
                              </Avatar>
                            )}
                            <Typography variant='body2'>
                              {member.user.name} {member.user.lastName}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {member.user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {parseTimestamp(member.createdAt || '')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`status.${member.status}`)}
                            sx={{
                              ...(CompanyUsersStatus.PENDING_COMPANY ===
                                member.status && {
                                backgroundColor: orange[200],
                                color: 'rgba(0, 0, 0, 0.87)'
                              })
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Stack
                            direction='row'
                            spacing='0.5rem'
                            justifyContent='flex-end'
                          >
                            <Button
                              variant='contained'
                              color='error'
                              startIcon={<CancelOutlinedIcon />}
                              onClick={() =>
                                NiceModal.show(RejectInvitationModal, {
                                  companyId,
                                  userId: member.user.id
                                })
                              }
                              sx={buttonStyles}
                            >
                              {t('common.reject')}
                            </Button>
                            <Button
                              variant='contained'
                              color='success'
                              startIcon={<CheckCircleOutlineOutlinedIcon />}
                              onClick={() => {
                                sessionStorage.setItem(
                                  'invitationEmail',
                                  member.user.email
                                )
                                navigate(
                                  `/companies/${companyId}/invitations/received/${member.user.id}`
                                )
                              }}
                              sx={buttonStyles}
                            >
                              {t('common.accept')}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1.5rem',
            marginTop: '4rem'
          }}
        >
          <img
            src={EmptyInvitations}
            alt='Empty Invitations'
            style={{
              width: '9.5rem',
              height: '9.5rem',
              objectFit: 'contain'
            }}
          />
          <Typography variant='h6' color='text.secondary'>
            {t('invitations_view.sent.empty.title')}
          </Typography>
          <Typography variant='body1'>
            {t('invitations_view.sent.empty.text')}
          </Typography>
          {canCreateMembers && (
            <Button
              variant='contained'
              startIcon={<PersonAddAltOutlinedIcon />}
              component={Link}
              sx={{
                textTransform: 'none',
                color: '#171B1E'
              }}
              to={`/companies/${companyId}/invitations/create`}
            >
              {t('invitations_view.sent.empty.button')}
            </Button>
          )}
        </Box>
      )}
    </>
  )
}
